import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { FileText, Scale, RotateCw } from "lucide-react";

const now = new Date();
const startDefault = `${now.getFullYear()}-01`;
const endDefault = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

export default function AdminPnL() {
  const [tab, setTab] = useState("pnl");
  const [from, setFrom] = useState(startDefault);
  const [to, setTo] = useState(endDefault);
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);

  const load = async () => {
    try {
      const [p, b] = await Promise.all([
        api.get(`/finance/pnl?from_month=${from}&to_month=${to}`),
        api.get("/finance/balance-sheet"),
      ]);
      setPnl(p.data);
      setBs(b.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const cls = "border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-1.5 text-sm";

  return (
    <div data-testid="admin-pnl" className="space-y-6">
      <div>
        <p className="overline">Finance · Statements</p>
        <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
          Financial Statements
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E2E8F0] dark:border-slate-700">
        {[["pnl", "P&L", FileText], ["bs", "Balance Sheet", Scale]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`stmt-tab-${k}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider transition inline-flex items-center gap-1.5 ${
                    tab === k
                      ? "border-b-2 border-[#C9A356] text-[#0A2540] dark:text-white font-medium"
                      : "text-[#475569] dark:text-slate-400 hover:text-[#0A2540]"
                  }`}>
            <Icon className="w-3 h-3" /> {label}
          </button>
        ))}
      </div>

      {tab === "pnl" && (
        <>
          <div className="card-pb p-4 flex items-end gap-3 flex-wrap">
            <div><label className="text-[10px] uppercase font-bold text-[#94A3B8] block">From</label>
              <input type="month" className={cls} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="pnl-from" />
            </div>
            <div><label className="text-[10px] uppercase font-bold text-[#94A3B8] block">To</label>
              <input type="month" className={cls} value={to} onChange={(e) => setTo(e.target.value)} data-testid="pnl-to" />
            </div>
            <button onClick={load} className="btn-accent text-sm inline-flex items-center gap-1" data-testid="pnl-refresh">
              <RotateCw className="w-3 h-3" /> Compute
            </button>
          </div>
          {pnl && (
            <div className="card-pb p-6">
              <StatementSection title="Revenue" rows={pnl.revenue} total={pnl.total_revenue} tone="good" />
              <StatementSection title="Expenses" rows={pnl.expense} total={pnl.total_expense} tone="bad" />
              <div className="border-t-2 border-[#0A2540] dark:border-white mt-4 pt-3 flex justify-between text-lg font-heading">
                <span>Net Income</span>
                <span className={`tabular-nums ${pnl.net_income >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid="pnl-net-income">
                  ${pnl.net_income.toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "bs" && bs && (
        <div className="card-pb p-6" data-testid="balance-sheet">
          <div className="text-xs text-[#94A3B8] mb-4">As of {new Date(bs.as_of).toLocaleDateString()}</div>
          <StatementSection title="Assets" rows={bs.assets} total={bs.total_assets} tone="good" />
          <StatementSection title="Liabilities" rows={bs.liabilities} total={bs.total_liabilities} tone="warn" />
          <StatementSection title="Equity" rows={bs.equity} total={bs.total_equity} tone="good" />
          <div className={`border-t-2 mt-4 pt-3 flex justify-between text-sm ${
            bs.balanced ? "border-emerald-500" : "border-red-500"
          }`}>
            <span className="font-bold">
              {bs.balanced ? "✓ Balanced" : "⚠ Not balanced"}
            </span>
            <span className="text-[#94A3B8]">
              Assets ${bs.total_assets.toLocaleString()} = Liab ${bs.total_liabilities.toLocaleString()} + Equity ${bs.total_equity.toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function StatementSection({ title, rows, total, tone }) {
  const color = tone === "good" ? "text-emerald-600" : tone === "bad" ? "text-red-500" : "text-amber-600";
  return (
    <div className="mb-4">
      <h3 className="text-sm font-bold text-[#0A2540] dark:text-white mb-2">{title}</h3>
      <ul className="text-sm">
        {(rows || []).length === 0 && <li className="text-xs text-[#94A3B8] italic py-1">— no activity —</li>}
        {(rows || []).map((r) => (
          <li key={r.code} className="flex justify-between py-1 border-b border-[#E2E8F0] dark:border-slate-700" data-testid={`stmt-row-${r.code}`}>
            <span><span className="font-mono text-[10px] text-[#94A3B8] mr-2">{r.code}</span>{r.name}</span>
            <span className="tabular-nums">${r.amount.toLocaleString()}</span>
          </li>
        ))}
      </ul>
      <div className={`text-right mt-1 text-sm font-medium ${color} tabular-nums`}>
        Total: ${total.toLocaleString()}
      </div>
    </div>
  );
}
